import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { patientRelations } from '@app/icare/db/query/patientRelations'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import {
  PatientMasterDataSheetStatusEnum,
  type PatientMasterDataSheetStatusType,
} from '@app/icare/db/schema/form/PatientMasterDataSheet'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Descriptions, Space, Table, Typography } from 'antd'
import { sql } from 'kysely'
import { jsonObjectFrom } from 'kysely/helpers/sqlite'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { resolveRecipientDisplayData } from '../patienten/$id/_edit/_documents/_forms/stammblatt/-utils/recipient'

export const Route = createFileRoute('/_app/_dashboards/master-data-sheets')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'patientForm.createdAt',
      'patientForm.createdBy',
      'patient.personal.lastName',
      'patientForm.status',
      'patientForm.updatedBy',
      'patientForm.updatedAt',
    ])
    .default('patientForm.createdAt')
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(PatientMasterDataSheetStatusEnum.$.zodEnum())).nullish(),
      createdBy: Q.in(z.string()).nullish(),
      updatedBy: Q.in(z.string()).nullish(),
    })
    .optional(),
})

const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())
const INITIAL_FILTERS = {
  'patientForm.status': F.in([PatientMasterDataSheetStatusEnum.new]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}
const useMasterDataQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

function RouteComponent() {
  const queryStore = useMasterDataQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const masterDataParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = masterDataParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .select(sb =>
          jsonObjectFrom(
            sb
              .selectFrom('patient')
              .select(['id', 'personal', 'contact', 'status', 'address'])
              .select([
                patientRelations.careServices(),
                patientRelations.pharmacies(),
                patientRelations.cooperationPartners(),
              ])
              .whereRef('patient.id', '=', 'patientForm.patientId'),
          ).as('patient'),
        )
        .where('patientForm.type', '=', PatientFormTypeEnum.MasterDataSheet)

        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$if(masterDataParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            masterDataParams.sortDir,
          ),
        )
        .$call(queryKit.filters(masterDataParams, ['patientForm.status', 'patientForm.type']))
        .$call(queryKit.search(masterDataParams))
        .$call(queryKit.deleted(masterDataParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$if(masterDataParams.sortBy !== 'patient.personal.lastName', qb => qb.$call(queryKit.sort(masterDataParams)))
        .$call(queryKit.paginate(masterDataParams)),
      params: masterDataParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: <Trans>Status</Trans>,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<PatientMasterDataSheetStatusType>
            value={record.status as PatientMasterDataSheetStatusType}
            statusEnum={PatientMasterDataSheetStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.createdAt',
        title: <Trans>Erstellt am</Trans>,
        sorter: true,
        render: (_, record) => formatDate(record.createdAt),
      },
      {
        key: 'patientForm.createdBy',
        title: <Trans>Erstellt von</Trans>,
        sorter: true,
        render: (_, record) => <UserInfo userId={record.createdBy} />,
      },
      {
        key: 'patientForm.recipient',
        title: <Trans>Empfänger</Trans>,
        sorter: false,
        render: (_, record) => {
          const recipientDisplayData = resolveRecipientDisplayData(record.patientId, record.form?.recipient, '-')
          return (
            <Typography.Text type={recipientDisplayData.isPlaceholder ? 'secondary' : undefined}>
              {recipientDisplayData.label}
            </Typography.Text>
          )
        },
      },
      {
        key: 'patientForm.id',
        title: t`Aktionen`,
        sorter: false,
        fixed: 'end',
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/stammblatt/$form"
            params={{ id: record.patientId, form: record.id }}
            size="small"
          >
            <Trans>zum Dokument</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useMasterDataQueryStore,
  )
  type RowRecord = ResultTypeFromQueryResult<typeof data>

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )
  return (
    <QueryFilterWrapper
      queryStore={useMasterDataQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          label: <Trans>Status des Stammblatts</Trans>,
          mode: 'multiple',
          options: PatientMasterDataSheetStatusEnum.$.options,
        },
      ]}
    >
      <Table<RowRecord>
        dataSource={data?.data ?? []}
        loading={isFetching}
        rowKey="id"
        pagination={data}
        onChange={queryStore.onChange}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: record => (
            <Descriptions
              column={1}
              size="small"
              style={{ paddingLeft: 32 }}
              items={[
                {
                  label: t`Erstellt von`,
                  key: 'createdBy',
                  children: <UserInfo userId={record.createdBy} />,
                },
                {
                  label: t`Erstellt am`,
                  key: 'createdAt',
                  children: formatDateTime(record.createdAt),
                },
                {
                  label: t`Geändert von`,
                  key: 'updatedBy',
                  children: <UserInfo userId={record.updatedBy} />,
                },
                {
                  label: t`Geändert am`,
                  key: 'updatedAt',
                  children: formatDateTime(record.updatedAt),
                },
              ]}
            />
          ),
        }}
      />
    </QueryFilterWrapper>
  )
}
